import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceProductVersionCatalogListItem } from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type VersionSortField = 'productCode' | 'versionNumber' | 'status' | 'publishedAtUtc';

@Component({
  standalone: true,
  selector: 'ain-insurance-product-versions-shell',
  imports: [AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, InfoHintComponent, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('insurance.productVersions.list.title')" [description]="t('insurance.productVersions.list.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.productVersions.list.help.title'"
        [descriptionKey]="'insurance.productVersions.list.help.description'"
        [bulletKeys]="[
          'insurance.productVersions.list.help.bullets.scope',
          'insurance.productVersions.list.help.bullets.navigation',
          'insurance.productVersions.list.help.bullets.live'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>{{ t('insurance.filters.title') }}</summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.productVersions.list.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.productVersions.states.empty')" [message]="t('insurance.productVersions.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('productCode')">{{ t('insurance.products.fields.code') }}</button></th>
                  <th>{{ t('insurance.products.fields.name') }}</th>
                  <th>{{ t('insurance.products.fields.lineOfBusiness') }}</th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('versionNumber')">{{ t('insurance.productVersions.fields.versionNumber') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('insurance.productVersions.fields.status') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('publishedAtUtc')">{{ t('insurance.productVersions.fields.publishedAt') }}</button></th>
                  <th>{{ t('insurance.productVersions.fields.minPremium') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>{{ row.productCode }}</td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row)">v{{ row.versionNumber }} — {{ row.productName }}</button></td>
                  <td>{{ row.lineOfBusiness }}</td>
                  <td>{{ row.versionNumber }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.publishedAtUtc ? (row.publishedAtUtc | ainDate) : '—' }}</td>
                  <td>{{ row.minPremium != null ? (row.minPremium | ainAmount) : '—' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;background:#fff;padding:12px}
    .panel{display:grid;gap:10px}
    .filters{border:1px solid #e5e7eb;border-radius:10px;padding:8px 10px;background:#f9fafb}
    .filter-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:8px}
    .filter-grid label{display:grid;gap:6px}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    .error{color:#b91c1c}
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceProductVersionsShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly rows = signal<InsuranceProductVersionCatalogListItem[]>([]);
  readonly selectedRows = signal<InsuranceProductVersionCatalogListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<VersionSortField>('productCode');
  readonly sortOrder = signal<SortOrder>(1);

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listProductVersions().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceProductVersionCatalogListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.rows()
      : this.rows().filter(row =>
        row.productCode.toLowerCase().includes(value)
        || row.productName.toLowerCase().includes(value)
        || row.lineOfBusiness.toLowerCase().includes(value)
        || String(row.versionNumber).includes(value));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceProductVersionCatalogListItem[] | InsuranceProductVersionCatalogListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row);
    }
  }

  openDetail(row: InsuranceProductVersionCatalogListItem): void {
    void this.router.navigate(['/insurance/products', row.productId, 'versions', row.id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-product-versions.csv',
      ['ProductCode', 'ProductName', 'LineOfBusiness', 'VersionNumber', 'Status', 'PublishedAtUtc', 'MinPremium'],
      rows.map(item => [
        item.productCode,
        item.productName,
        item.lineOfBusiness,
        String(item.versionNumber),
        item.status,
        item.publishedAtUtc ?? '',
        item.minPremium?.toString() ?? ''
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: VersionSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  liveStatusKey = liveStatusKey;

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
